import type { Component } from "vue";
import type { FieldDefinition, FieldType } from "@/shared/contracts/structure";
import { say } from "../say";
import ChoiceWidget from "../widgets/ChoiceWidget.vue";
import DateWidget from "../widgets/DateWidget.vue";
import FileWidget from "../widgets/FileWidget.vue";
import JsonWidget from "../widgets/JsonWidget.vue";
import MultiChoiceWidget from "../widgets/MultiChoiceWidget.vue";
import NumberWidget from "../widgets/NumberWidget.vue";
import RatingWidget from "../widgets/RatingWidget.vue";
import ReferenceWidget from "../widgets/ReferenceWidget.vue";
import SignatureWidget from "../widgets/SignatureWidget.vue";
import SwitchWidget from "../widgets/SwitchWidget.vue";
import TextAreaWidget from "../widgets/TextAreaWidget.vue";
import TextWidget from "../widgets/TextWidget.vue";

/**
 * Which widget renders which field, and how a value is displayed.
 *
 * A REGISTRY, not a switch (I-F4). Adding an input is one component file plus
 * one line in `WIDGETS` — it never touches the renderer, never touches another
 * widget, and never creates a special case. When adding a type would require any
 * of those, the modelling is wrong.
 *
 * Nothing in this file — or anywhere under `dynamic/` — may mention a specific
 * entity. The first `if (entity === 'patient')` is the first step back toward 247
 * hand-written pages (F-ADR-005).
 */
export type WidgetKind =
	| "text"
	| "textarea"
	| "number"
	| "switch"
	| "date"
	| "choice"
	| "multichoice"
	| "json"
	| "file"
	| "reference"
	| "signature"
	| "rating";

/**
 * The map is exhaustive over `FieldType`, so adding a type to the backend
 * catalog produces a COMPILE ERROR here rather than a blank cell in production.
 */
const KIND: Record<FieldType, WidgetKind> = {
	text: "text",
	long_text: "textarea",
	number: "number",
	integer: "number",
	currency: "number",
	boolean: "switch",
	date: "date",
	datetime: "date",
	time: "date",
	select: "choice",
	multi_select: "multichoice",
	email: "text",
	phone: "text",
	url: "text",
	// One widget for both: the difference is company vs person, and the validator
	// comes from the tenant's COUNTRY, not from the type (ADR-018). There is
	// deliberately no cpf/cnpj widget.
	tax_id: "text",
	national_id: "text",
	json: "json",
	file: "file",
	// A signature is a `file` semantically and a canvas visually — which is
	// exactly why it is one type plus one widget and nothing else (§8).
	signature: "signature",
	rating: "rating",
	ref_entity: "reference",
	ref_contact: "reference",
	ref_user: "reference",
};

export const WIDGETS: Record<WidgetKind, Component> = {
	text: TextWidget,
	textarea: TextAreaWidget,
	number: NumberWidget,
	switch: SwitchWidget,
	date: DateWidget,
	choice: ChoiceWidget,
	multichoice: MultiChoiceWidget,
	json: JsonWidget,
	file: FileWidget,
	reference: ReferenceWidget,
	signature: SignatureWidget,
	rating: RatingWidget,
	// No `subform` here on purpose: no FieldType maps to it — a subform stands in
	// for a RELATION, and `DynamicForm` renders it directly. Registering it would
	// also close an import cycle: the subform's add row resolves its own child
	// widgets through this very registry.
};

export function widgetFor(type: FieldType): WidgetKind {
	// An unknown type must not break the screen: it renders as text and, in
	// development, says so. A metadata addition should never blank a page.
	const kind = KIND[type];
	if (!kind) {
		if (import.meta.env.DEV) console.warn(`[dynamic] no widget for field type "${type}"`);
		return "text";
	}
	return kind;
}

/** The component to render for a field. */
export function resolveWidget(field: FieldDefinition): Component {
	return WIDGETS[widgetFor(field.type)] ?? WIDGETS.text;
}

/**
 * Whether the value is COMPUTED rather than typed (§3.8).
 *
 * A flag in `config` and not a `FieldType`, which is the decision that matters
 * here: `KIND` above is exhaustive over `FieldType` on purpose, and a `derived`
 * type would owe it a widget — while a derived total is a `currency` and a
 * derived count is an `integer`, with the same widget and the same formatting.
 * What changes is not how it LOOKS, it is who fills it in.
 *
 * So the resolver decides reading versus input, and no renderer grows a case for
 * a particular entity (F-ADR-005).
 */
export function isDerivedField(field: FieldDefinition): boolean {
	const derived = field.config?.derived;
	return typeof derived === "object" && derived !== null && !Array.isArray(derived);
}

/**
 * "Calculado a partir de Itens do pedido" — pelo NOME da coleção filha (quem chama passa
 * o rótulo do filho, não o do ponteiro para o pai: era "Orçamento"), nunca pelo código
 * da tabela (`card_dependent` não diz nada a quem atende). Sem o nome à mão, a frase
 * genérica.
 */
export function describeDerived(
	field: FieldDefinition,
	relations: ReadonlyArray<{ toEntityCode: string; label: string }> = [],
): string {
	const derived = field.config?.derived as { from?: unknown } | undefined;
	const from = relations.find((r) => r.toEntityCode === derived?.from)?.label;
	return from ? say("computedFrom", { from }) : say("computedAuto");
}

/** The row formula, when the field has one — `subtotal = qtd * preco` (§3.8). */
export function formulaOf(field: FieldDefinition): string | null {
	const formula = field.config?.formula;
	if (typeof formula !== "string") return null;
	const expr = formula.trim();
	return expr.length > 0 ? expr : null;
}

export function isFormulaField(field: FieldDefinition): boolean {
	return formulaOf(field) !== null;
}

/**
 * Computed by EITHER path — the row's own arithmetic or the children's
 * aggregate. One name because every renderer decision they share ("read, never
 * an input; neither validated nor sent") is about the family, not the flavour.
 */
export function isComputedField(field: FieldDefinition): boolean {
	return isDerivedField(field) || isFormulaField(field);
}

/**
 * "Calculado a partir de Valor e Pago" — pelos RÓTULOS dos campos que a fórmula
 * lê. A expressão é SQL (`coalesce(price, 0) - …`) e, mostrada crua sob o valor,
 * era código na cara de quem atende (E2E estética).
 */
export function describeFormula(
	field: FieldDefinition,
	fields: ReadonlyArray<Pick<FieldDefinition, "code" | "label">> = [],
): string {
	const words = new Set(formulaOf(field)?.match(/[a-z_][a-z0-9_]*/gi) ?? []);
	const read = fields.filter((f) => f.code !== field.code && words.has(f.code)).map((f) => f.label);
	return read.length ? say("computedFrom", { from: read.join(", ") }) : say("computedAuto");
}

/**
 * A synthetic field standing in for a 1:N relation.
 *
 * The layout addresses a subform by the RELATION code, and the renderer has to
 * hand the widget something field-shaped. Manufacturing it here keeps
 * `DynamicForm` from growing a second code path for "not really a field".
 */
export function relationField(relation: {
	code: string;
	label: string;
	toEntityCode: string;
	required: boolean;
}): FieldDefinition {
	return {
		id: `relation:${relation.code}`,
		code: relation.code,
		label: relation.label,
		// `json` only as the carrier type; the resolver is bypassed for these.
		type: "json",
		required: relation.required,
		config: { entity: relation.toEntityCode },
		helpText: null,
		sortOrder: 999,
		showInList: false,
		archived: false,
	};
}

// Re-exported so a caller that already imports the registry does not need to
// know these moved. The dependency runs one way: registry → config.
export {
	type DisplayContext,
	displayOf,
	formatValue,
	optionsFor,
} from "./field-config";
